import React from "react"
import PropTypes from "prop-types"
import { Icon } from "ui/Icon"
import styles from "./index.module.css"

const MINUTES_PER_HOUR = 60

/**
 * "08:30" -> 510. The feed sends 24-hour times, or null for a closure that runs all day.
 *
 * The empty check is load-bearing: `Number("")` is 0, not NaN, so without it an all-day closure
 * parses as 00:00-00:00 and collapses to nothing.
 */
const toMinutes = (time) => {
  if (!time) return null
  const [hour, minute] = String(time).split(":")
  const hours = Number(hour)
  if (!Number.isFinite(hours)) return null
  return hours * MINUTES_PER_HOUR + (Number(minute) || 0)
}

/**
 * Where a closure sits along a resource row, as percentages of the visible day.
 *
 * Clamped to the grid's own window: a closure that starts before the first hour shown, or runs
 * past the last, is drawn to the edge rather than off it.
 */
export const getBlackoutSpan = (blackout, startHour, endHour) => {
  const windowStart = startHour * MINUTES_PER_HOUR
  const windowEnd = (endHour + 1) * MINUTES_PER_HOUR
  const totalMinutes = windowEnd - windowStart
  if (totalMinutes <= 0) return null

  const start = toMinutes(blackout?.start_time)
  const end = toMinutes(blackout?.end_time)
  // No window means all day — the closure owns the row.
  if (start === null || end === null) return { left: 0, width: 100 }

  const clampedStart = Math.max(start, windowStart)
  const clampedEnd = Math.min(end, windowEnd)
  if (clampedEnd <= clampedStart) return null

  return {
    left: ((clampedStart - windowStart) / totalMinutes) * 100,
    width: ((clampedEnd - clampedStart) / totalMinutes) * 100
  }
}

/**
 * One closure drawn on the resource row it belongs to, in the day grid.
 *
 * A crew member being off is a fact about that crew member, so the design marks their row for the
 * hours they are away — a dark label bar over shaded hours — and leaves everyone else's row
 * untouched. The whole-grid shading is kept for closures that really do apply to everyone, which
 * is the business and listing scopes.
 */
const ResourceBlackoutBar = ({ blackout, startHour, endHour, onOpenDetails }) => {
  const span = getBlackoutSpan(blackout, startHour, endHour)
  if (!span) return null

  const position = { left: `${span.left}%`, width: `${span.width}%` }

  return (
    <>
      <div aria-hidden className={styles.shade} style={position} />
      <button type="button" className={styles.bar} style={position} onClick={onOpenDetails} title={blackout.reason || blackout.label}>
        <span className={styles.label}>{blackout.label}</span>
        <Icon name="ChevronRight" width={14} height={14} color="currentColor" />
      </button>
    </>
  )
}

ResourceBlackoutBar.propTypes = {
  blackout: PropTypes.shape({
    label: PropTypes.string,
    reason: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string
  }).isRequired,
  /** The grid's visible window, so a closure outside it is clamped rather than overflowing. */
  startHour: PropTypes.number.isRequired,
  endHour: PropTypes.number.isRequired,
  onOpenDetails: PropTypes.func
}

export default ResourceBlackoutBar
